// The Go PM API has no enforced auth yet (see src/apiPm.js), so task
// mutations that need to know "who did this" (activity log entries) pass
// the logged-in CRM user's id explicitly, sourced from the same localStorage
// entry the main app already uses for auth state.
export const getCurrentUserId = () => {
  try {
    const stored = JSON.parse(localStorage.getItem('user') || 'null');
    return stored?.id ? String(stored.id) : '';
  } catch {
    return '';
  }
};

// Mirrors the Go PM backend's automatic Stage computation (task_stage CASE in
// CrmProjects/CrmProjectByID — see repository.go). Stage is never set
// manually; it's derived from each project's active pm_project_tasks mix.
// Priority: Blocked > Completed > Review > In Progress > Planning.
// 'In Progress' was 'Fieldwork' until 2026-07-22 — see normalizeStageLabel
// below for the read-time compat shim covering any lingering old value.
export const PROJECT_STAGES = [
  'Planning',
  'In Progress',
  'Review',
  'Blocked',
  'Completed',
];

// normalizeStageLabel maps a project's raw `stage` value to the current
// label set — today this only ever rewrites the pre-2026-07-22 'Fieldwork'
// value to 'In Progress', so a stale cached response or an old build still
// showing on some tab never surfaces the retired label.
export const normalizeStageLabel = (stage) => (stage === 'Fieldwork' ? 'In Progress' : stage);

export const TASK_STAGES = [
  'Backlog',
  'To Do',
  'In Progress',
  'Review',
  'UAT',
  'Final Report',
  'Done',
];

export const HEALTH_OPTIONS = ['On Track', 'At Risk', 'Delayed', 'Blocked', 'Completed'];

export const shouldUseSampleData = process.env.NODE_ENV !== 'production';

export const sampleProjects = [
  {
    id: 'sample-1',
    title: 'ISO/IEC 27001 Surveillance Audit',
    clientName: 'CBQA Global',
    owner: 'Harmanto',
    members: ['HN', 'KL', 'HB'],
    stage: 'In Progress',
    progress: 68,
    nextDeadline: '18/06/2026',
    health: 'On Track',
    updatedAt: '11/06/2026',
  },
  {
    id: 'sample-2',
    title: 'PCI-DSS Remediation Review',
    clientName: 'One Alpha',
    owner: 'Audit Team',
    members: ['AR', 'NY'],
    stage: 'Review',
    progress: 42,
    nextDeadline: '14/06/2026',
    health: 'At Risk',
    updatedAt: '10/06/2026',
  },
  {
    id: 'sample-3',
    title: 'Internal Audit Platform Rollout',
    clientName: 'Internal Initiative',
    owner: 'Project Office',
    members: ['PM', 'IT', 'QA'],
    stage: 'UAT',
    progress: 82,
    nextDeadline: '21/06/2026',
    health: 'On Track',
    updatedAt: '09/06/2026',
  },
];

export const sampleDetail = {
  id: 'sample',
  title: 'ISO/IEC 27001 Surveillance Audit',
  clientName: 'CBQA Global',
  owner: 'Harmanto',
  health: 'On Track',
  stage: 'In Progress',
  progress: 68,
  auditDate: '18/06/2026',
  deadline: '28/06/2026',
  projectStartDate: '01/06/2026',
  projectEndDate: '30/06/2027',
  scope: 'IT Audit delivery monitoring for ISO/IEC 27001 surveillance engagement.',
  team: [
    { id: 1, name: 'Harmanto', role: 'Project Owner', openTasks: 4 },
    { id: 2, name: 'Audit Team', role: 'Auditor', openTasks: 6 },
    { id: 3, name: 'QA Reviewer', role: 'Reviewer', openTasks: 2 },
  ],
  tasks: [
    {
      id: 1,
      title: 'Confirm audit scope and schedule',
      description: 'Validate audit scope, key contacts, audit window, and expected deliverables before execution starts.',
      stage: 'Backlog',
      owner: 'Harmanto',
      assignee_name: 'Harmanto',
      planStartDate: '2026-06-12',
      planFinishDate: '2026-06-14',
      actualStartDate: '2026-06-12',
      actualFinishDate: '2026-06-14',
      startDate: '2026-06-12',
      dueDate: '2026-06-14',
      progress: 100,
      progress_pct: 100,
      priority_id: 2,
      status_key: 'done',
      status_title: 'Done',
      priority: 'Medium',
      status: 'done',
      checklist: ['Scope confirmed', 'Audit window approved', 'Kickoff invite sent'],
      comments: 2,
    },
    {
      id: 11,
      title: 'Confirm auditee contacts',
      description: 'Verify PIC, escalation path, and meeting availability for the audit window.',
      stage: 'Backlog',
      owner: 'Harmanto',
      assignee_name: 'Harmanto',
      parent_task_id: 1,
      parentTaskId: 1,
      planStartDate: '2026-06-12',
      planFinishDate: '2026-06-13',
      actualStartDate: '2026-06-12',
      actualFinishDate: '2026-06-13',
      startDate: '2026-06-12',
      dueDate: '2026-06-13',
      progress: 100,
      progress_pct: 100,
      priority_id: 2,
      status_key: 'done',
      status_title: 'Done',
      priority: 'Medium',
      status: 'done',
      checklist: ['PIC verified', 'Escalation path confirmed'],
      comments: 1,
    },
    {
      id: 2,
      title: 'Collect evidence from auditee',
      description: 'Track evidence request list and ensure required documents are received from the auditee.',
      stage: 'To Do',
      owner: 'Audit Team',
      assignee_name: 'Audit Team',
      planStartDate: '2026-06-14',
      planFinishDate: '2026-06-16',
      actualStartDate: '2026-06-15',
      actualFinishDate: '',
      startDate: '2026-06-14',
      dueDate: '2026-06-16',
      progress: 30,
      progress_pct: 30,
      priority_id: 3,
      status_key: 'to_do',
      status_title: 'To Do',
      priority: 'High',
      status: 'to_do',
      checklist: ['Evidence list shared', 'Access confirmed', 'Initial evidence received'],
      comments: 4,
    },
    {
      id: 21,
      title: 'Upload evidence request list',
      description: 'Prepare the request list and share it with the auditee.',
      stage: 'To Do',
      owner: 'Audit Team',
      assignee_name: 'Audit Team',
      parent_task_id: 2,
      parentTaskId: 2,
      planStartDate: '2026-06-14',
      planFinishDate: '2026-06-14',
      actualStartDate: '2026-06-15',
      actualFinishDate: '2026-06-15',
      startDate: '2026-06-14',
      dueDate: '2026-06-14',
      progress: 100,
      progress_pct: 100,
      priority_id: 3,
      status_key: 'done',
      status_title: 'Done',
      priority: 'High',
      status: 'done',
      checklist: ['Request list uploaded'],
      comments: 1,
    },
    {
      id: 22,
      title: 'Follow up pending evidence',
      description: 'Monitor late submissions and update the evidence tracker.',
      stage: 'To Do',
      owner: 'Audit Team',
      assignee_name: 'Audit Team',
      parent_task_id: 2,
      parentTaskId: 2,
      planStartDate: '2026-06-15',
      planFinishDate: '2026-06-16',
      actualStartDate: '',
      actualFinishDate: '',
      startDate: '2026-06-15',
      dueDate: '2026-06-16',
      progress: 20,
      progress_pct: 20,
      priority_id: 3,
      status_key: 'in_progress',
      status_title: 'In Progress',
      priority: 'High',
      status: 'in_progress',
      checklist: ['Evidence tracker updated'],
      comments: 2,
    },
    {
      id: 3,
      title: 'Execute fieldwork checklist',
      description: 'Perform audit procedures, document observations, and capture supporting evidence.',
      stage: 'In Progress',
      owner: 'Audit Team',
      assignee_name: 'Audit Team',
      planStartDate: '2026-06-16',
      planFinishDate: '2026-06-18',
      actualStartDate: '2026-06-17',
      actualFinishDate: '',
      startDate: '2026-06-16',
      dueDate: '2026-06-18',
      progress: 65,
      progress_pct: 65,
      priority_id: 3,
      status_key: 'in_progress',
      status_title: 'In Progress',
      priority: 'High',
      status: 'in_progress',
      checklist: ['Controls sampled', 'Evidence reviewed', 'Working paper updated'],
      comments: 5,
    },
    {
      id: 4,
      title: 'Review draft findings',
      description: 'Review draft findings for accuracy, impact, and remediation recommendation quality.',
      stage: 'Review',
      owner: 'QA Reviewer',
      assignee_name: 'QA Reviewer',
      planStartDate: '2026-06-19',
      planFinishDate: '2026-06-22',
      actualStartDate: '',
      actualFinishDate: '',
      startDate: '2026-06-19',
      dueDate: '2026-06-22',
      progress: 40,
      progress_pct: 40,
      priority_id: 2,
      status_key: 'in_review',
      status_title: 'In Review',
      priority: 'Medium',
      status: 'in_review',
      checklist: ['Findings validated', 'Recommendation reviewed', 'QA signoff completed'],
      comments: 1,
    },
    {
      id: 5,
      title: 'Client confirmation and final report',
      description: 'Confirm management responses and prepare the final audit report package.',
      stage: 'UAT',
      owner: 'Harmanto',
      assignee_name: 'Harmanto',
      planStartDate: '2026-06-23',
      planFinishDate: '2026-06-26',
      actualStartDate: '',
      actualFinishDate: '',
      startDate: '2026-06-23',
      dueDate: '2026-06-26',
      progress: 10,
      progress_pct: 10,
      priority_id: 1,
      status_key: 'to_do',
      status_title: 'To Do',
      priority: 'Low',
      status: 'to_do',
      checklist: ['Management response received', 'Report finalized', 'Closure email sent'],
      comments: 0,
    },
    {
      id: 31,
      title: 'Prepare audit working paper package',
      description: 'Compile procedure templates, sampling sheets, and evidence references for fieldwork execution.',
      stage: 'In Progress',
      owner: 'Audit Team',
      assignee_name: 'Audit Team',
      parent_task_id: 3,
      parentTaskId: 3,
      planStartDate: '2026-06-16',
      planFinishDate: '2026-06-17',
      actualStartDate: '2026-06-16',
      actualFinishDate: '2026-06-18',
      startDate: '2026-06-16',
      dueDate: '2026-06-17',
      progress: 100,
      progress_pct: 100,
      priority_id: 3,
      status_key: 'done',
      status_title: 'Done',
      priority: 'High',
      status: 'done',
      checklist: ['Working paper created', 'Sample size confirmed'],
      comments: 2,
    },
    {
      id: 32,
      title: 'Interview process owners',
      description: 'Conduct interviews and capture process walkthrough notes.',
      stage: 'In Progress',
      owner: 'Audit Team',
      assignee_name: 'Audit Team',
      parent_task_id: 3,
      parentTaskId: 3,
      planStartDate: '2026-06-17',
      planFinishDate: '2026-06-18',
      actualStartDate: '2026-06-17',
      actualFinishDate: '',
      startDate: '2026-06-17',
      dueDate: '2026-06-18',
      progress: 75,
      progress_pct: 75,
      priority_id: 3,
      status_key: 'in_progress',
      status_title: 'In Progress',
      priority: 'High',
      status: 'in_progress',
      checklist: ['Interview schedule confirmed', 'Walkthrough notes drafted'],
      comments: 3,
    },
    {
      id: 33,
      title: 'Validate control evidence',
      description: 'Match received evidence against control objectives and mark gaps.',
      stage: 'In Progress',
      owner: 'Audit Team',
      assignee_name: 'Audit Team',
      parent_task_id: 3,
      parentTaskId: 3,
      planStartDate: '2026-06-18',
      planFinishDate: '2026-06-20',
      actualStartDate: '2026-06-20',
      actualFinishDate: '',
      startDate: '2026-06-18',
      dueDate: '2026-06-20',
      progress: 45,
      progress_pct: 45,
      priority_id: 4,
      status_key: 'in_progress',
      status_title: 'In Progress',
      priority: 'Critical',
      status: 'in_progress',
      checklist: ['Evidence mapped', 'Gaps tagged'],
      comments: 4,
    },
    {
      id: 41,
      title: 'Perform QA review round 1',
      description: 'Review working paper completeness before draft findings are issued.',
      stage: 'Review',
      owner: 'QA Reviewer',
      assignee_name: 'QA Reviewer',
      parent_task_id: 4,
      parentTaskId: 4,
      planStartDate: '2026-06-19',
      planFinishDate: '2026-06-20',
      actualStartDate: '2026-06-18',
      actualFinishDate: '2026-06-20',
      startDate: '2026-06-19',
      dueDate: '2026-06-20',
      progress: 100,
      progress_pct: 100,
      priority_id: 2,
      status_key: 'done',
      status_title: 'Done',
      priority: 'Medium',
      status: 'done',
      checklist: ['Working paper checked', 'Review notes logged'],
      comments: 1,
    },
    {
      id: 42,
      title: 'Clarify draft observations',
      description: 'Resolve auditor and reviewer comments for draft observations.',
      stage: 'Review',
      owner: 'QA Reviewer',
      assignee_name: 'QA Reviewer',
      parent_task_id: 4,
      parentTaskId: 4,
      planStartDate: '2026-06-20',
      planFinishDate: '2026-06-22',
      actualStartDate: '2026-06-22',
      actualFinishDate: '',
      startDate: '2026-06-20',
      dueDate: '2026-06-22',
      progress: 55,
      progress_pct: 55,
      priority_id: 3,
      status_key: 'in_review',
      status_title: 'In Review',
      priority: 'High',
      status: 'in_review',
      checklist: ['Reviewer comments assigned', 'Observation wording updated'],
      comments: 2,
    },
    {
      id: 51,
      title: 'Collect management response',
      description: 'Receive response and target remediation date from client management.',
      stage: 'UAT',
      owner: 'Harmanto',
      assignee_name: 'Harmanto',
      parent_task_id: 5,
      parentTaskId: 5,
      planStartDate: '2026-06-23',
      planFinishDate: '2026-06-24',
      actualStartDate: '',
      actualFinishDate: '',
      startDate: '2026-06-23',
      dueDate: '2026-06-24',
      progress: 0,
      progress_pct: 0,
      priority_id: 2,
      status_key: 'to_do',
      status_title: 'To Do',
      priority: 'Medium',
      status: 'to_do',
      checklist: ['Response template sent'],
      comments: 0,
    },
    {
      id: 52,
      title: 'Prepare final report package',
      description: 'Assemble final report, attachments, and closure communication.',
      stage: 'Final Report',
      owner: 'Harmanto',
      assignee_name: 'Harmanto',
      parent_task_id: 5,
      parentTaskId: 5,
      planStartDate: '2026-06-25',
      planFinishDate: '2026-06-26',
      actualStartDate: '',
      actualFinishDate: '',
      startDate: '2026-06-25',
      dueDate: '2026-06-26',
      progress: 0,
      progress_pct: 0,
      priority_id: 1,
      status_key: 'to_do',
      status_title: 'To Do',
      priority: 'Low',
      status: 'to_do',
      checklist: ['Report template prepared'],
      comments: 0,
    },
    {
      id: 6,
      title: 'Risk and issue control',
      description: 'Track schedule risk, evidence blockers, and escalation items.',
      stage: 'Review',
      owner: 'Project Office',
      assignee_name: 'Project Office',
      planStartDate: '2026-06-13',
      planFinishDate: '2026-06-24',
      actualStartDate: '2026-06-14',
      actualFinishDate: '',
      startDate: '2026-06-13',
      dueDate: '2026-06-24',
      progress: 60,
      progress_pct: 60,
      priority_id: 3,
      status_key: 'in_progress',
      status_title: 'In Progress',
      priority: 'High',
      status: 'in_progress',
      checklist: ['Risk log opened', 'Escalations tracked'],
      comments: 3,
    },
    {
      id: 61,
      title: 'Create risk register',
      description: 'Create and categorize audit delivery risks.',
      stage: 'Review',
      owner: 'Project Office',
      assignee_name: 'Project Office',
      parent_task_id: 6,
      parentTaskId: 6,
      planStartDate: '2026-06-13',
      planFinishDate: '2026-06-14',
      actualStartDate: '2026-06-14',
      actualFinishDate: '2026-06-14',
      startDate: '2026-06-13',
      dueDate: '2026-06-14',
      progress: 100,
      progress_pct: 100,
      priority_id: 2,
      status_key: 'done',
      status_title: 'Done',
      priority: 'Medium',
      status: 'done',
      checklist: ['Risk register created'],
      comments: 1,
    },
    {
      id: 62,
      title: 'Escalate overdue evidence',
      description: 'Escalate missing evidence to client PIC and project owner.',
      stage: 'Review',
      owner: 'Project Office',
      assignee_name: 'Project Office',
      parent_task_id: 6,
      parentTaskId: 6,
      planStartDate: '2026-06-17',
      planFinishDate: '2026-06-19',
      actualStartDate: '2026-06-20',
      actualFinishDate: '',
      startDate: '2026-06-17',
      dueDate: '2026-06-19',
      progress: 50,
      progress_pct: 50,
      priority_id: 4,
      status_key: 'in_progress',
      status_title: 'In Progress',
      priority: 'Critical',
      status: 'in_progress',
      checklist: ['Escalation email sent', 'Client response pending'],
      comments: 5,
    },
    {
      id: 7,
      title: 'Stakeholder communication',
      description: 'Maintain project communication cadence with client and internal teams.',
      stage: 'In Progress',
      owner: 'Harmanto',
      assignee_name: 'Harmanto',
      planStartDate: '2026-06-12',
      planFinishDate: '2026-06-26',
      actualStartDate: '2026-06-12',
      actualFinishDate: '',
      startDate: '2026-06-12',
      dueDate: '2026-06-26',
      progress: 70,
      progress_pct: 70,
      priority_id: 2,
      status_key: 'in_progress',
      status_title: 'In Progress',
      priority: 'Medium',
      status: 'in_progress',
      checklist: ['Weekly update sent', 'Action items tracked'],
      comments: 6,
    },
    {
      id: 71,
      title: 'Send weekly status update',
      description: 'Publish weekly progress, blockers, and next actions.',
      stage: 'In Progress',
      owner: 'Harmanto',
      assignee_name: 'Harmanto',
      parent_task_id: 7,
      parentTaskId: 7,
      planStartDate: '2026-06-14',
      planFinishDate: '2026-06-14',
      actualStartDate: '2026-06-13',
      actualFinishDate: '2026-06-13',
      startDate: '2026-06-14',
      dueDate: '2026-06-14',
      progress: 100,
      progress_pct: 100,
      priority_id: 2,
      status_key: 'done',
      status_title: 'Done',
      priority: 'Medium',
      status: 'done',
      checklist: ['Status update sent'],
      comments: 1,
    },
    {
      id: 72,
      title: 'Prepare closing meeting deck',
      description: 'Create draft closing meeting slides and finding summary.',
      stage: 'Final Report',
      owner: 'Harmanto',
      assignee_name: 'Harmanto',
      parent_task_id: 7,
      parentTaskId: 7,
      planStartDate: '2026-06-24',
      planFinishDate: '2026-06-25',
      actualStartDate: '',
      actualFinishDate: '',
      startDate: '2026-06-24',
      dueDate: '2026-06-25',
      progress: 10,
      progress_pct: 10,
      priority_id: 2,
      status_key: 'to_do',
      status_title: 'To Do',
      priority: 'Medium',
      status: 'to_do',
      checklist: ['Deck outline drafted'],
      comments: 0,
    },
    {
      id: 8,
      title: 'Certificate readiness review',
      description: 'Validate final readiness items before certificate decision handoff.',
      stage: 'Final Report',
      owner: 'QA Reviewer',
      assignee_name: 'QA Reviewer',
      planStartDate: '2026-06-26',
      planFinishDate: '2026-06-28',
      actualStartDate: '',
      actualFinishDate: '',
      startDate: '2026-06-26',
      dueDate: '2026-06-28',
      progress: 0,
      progress_pct: 0,
      priority_id: 1,
      status_key: 'to_do',
      status_title: 'To Do',
      priority: 'Low',
      status: 'to_do',
      checklist: ['Readiness checklist pending'],
      comments: 0,
    },
  ],
  activity: [
    {
      id: 1,
      type: 'Task',
      title: 'Task moved to In Progress',
      description: 'Execute fieldwork checklist moved from To Do to In Progress.',
      actor: 'Audit Team',
      time: 'Today, 10:30',
      from: 'To Do',
      to: 'In Progress',
    },
    {
      id: 2,
      type: 'Health',
      title: 'Project health updated',
      description: 'Project marked as On Track after schedule review.',
      actor: 'Harmanto',
      time: 'Yesterday, 16:20',
      from: 'At Risk',
      to: 'On Track',
    },
    {
      id: 3,
      type: 'Team',
      title: 'Team member assigned',
      description: 'QA Reviewer added to Review stage.',
      actor: 'Project Office',
      time: '10/06/2026',
      from: 'Unassigned',
      to: 'QA Reviewer',
    },
    {
      id: 4,
      type: 'Comment',
      title: 'Evidence request clarified',
      description: 'Auditee requested clarification for access control evidence.',
      actor: 'Audit Team',
      time: '10/06/2026',
      from: null,
      to: null,
    },
  ],
};

export const emptyProjectDetail = {
  ...sampleDetail,
  id: '',
  title: 'Project unavailable',
  clientName: '-',
  owner: '-',
  health: '-',
  stage: '-',
  progress: 0,
  auditDate: '-',
  deadline: '-',
  projectStartDate: '-',
  projectEndDate: '-',
  scope: 'Project detail is not available yet.',
  team: [],
  tasks: [],
  activity: [],
};

export const withTimeout = (promise, timeoutMs = 8000) =>
  Promise.race([
    promise,
    new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Request timeout')), timeoutMs);
    }),
  ]);

export const getSafeApiErrorMessage = (error, fallback = 'Project data is temporarily unavailable.') => {
  const rawMessage = error?.response?.data?.message || error?.message || '';
  const lowerMessage = String(rawMessage).toLowerCase();
  const isTechnicalMessage =
    lowerMessage.includes('preparedstatement') ||
    lowerMessage.includes('psqlexception') ||
    lowerMessage.includes('sql') ||
    lowerMessage.includes('select ') ||
    lowerMessage.includes('join ') ||
    lowerMessage.includes('column ') ||
    lowerMessage.includes('nested exception') ||
    lowerMessage.length > 180;

  if (isTechnicalMessage) return fallback;
  return rawMessage || fallback;
};

export const clampProgress = (value) => {
  const number = Number(value ?? 0);
  return Number.isFinite(number) ? Math.min(Math.max(number, 0), 100) : 0;
};

export const getHealthTone = (health) => {
  const value = String(health || '').toLowerCase();
  if (value.includes('blocked') || value.includes('delayed')) return 'danger';
  if (value.includes('risk')) return 'warning';
  if (value.includes('complete') || value.includes('closed')) return 'success';
  return 'success';
};

// Tone per automatic Stage value (see PROJECT_STAGES above) — keeps the
// Blocked > Completed > Review > In Progress > Planning priority visually
// distinct instead of a single flat badge color for every stage.
export const getStageTone = (stage) => {
  switch (stage) {
    case 'Blocked': return 'danger';
    case 'Completed': return 'success';
    case 'Review': return 'warning';
    case 'In Progress': return 'blue';
    default: return 'secondary';
  }
};

export const getPriorityTone = (priority) => {
  const value = String(priority || '').toLowerCase();
  if (value.includes('high')) return 'danger';
  if (value.includes('medium')) return 'warning';
  return 'success';
};

// getLoadTone maps a Team Workload row's loadStatus (Ringan/Sedang/Berat —
// see the PM Dashboard's Go backend loadStatus() rule) to a badge tone.
export const getLoadTone = (loadStatus) => {
  switch (loadStatus) {
    case 'Berat': return 'danger';
    case 'Sedang': return 'warning';
    case 'Ringan': return 'success';
    default: return 'secondary';
  }
};

export const getTaskStatusTone = (status) => {
  const value = String(status || '').toLowerCase();
  if (value.includes('risk') || value.includes('overdue') || value.includes('blocked')) return 'danger';
  if (value.includes('progress')) return 'primary';
  if (value.includes('done') || value.includes('complete')) return 'success';
  return 'secondary';
};

const getMemberInitial = (member) => {
  if (typeof member === 'string') return member.slice(0, 2).toUpperCase();
  const name = member.name || member.username || member.email || 'NA';
  return name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
};

export const normalizeProject = (project, index) => {
  const members = project.members || project.teamMembers || project.team_members || [];

  return {
    id: project.id || project.projectId || `project-${index}`,
    title: project.title || project.name || project.projectName || 'Untitled project',
    clientName: project.clientName || project.client_name || project.client || project.companyName || 'Internal Initiative',
    owner: project.ownerName || project.owner || project.pic || project.createdBy || 'Unassigned',
    pic: project.pic || project.picName || project.pic_name || project.contactName || '-',
    picUserId: project.picUserId || project.pic_user_id || '',
    members: Array.isArray(members) ? members.map(getMemberInitial) : [],
    stage: normalizeStageLabel(project.stage || project.currentStage || project.current_stage || 'Planning'),
    progress: clampProgress(project.progress ?? project.completionRate ?? project.completion_rate),
    nextDeadline: project.nextDeadline || project.next_deadline || project.deadline || project.dueDate || '-',
    health: project.health || project.statusHealth || project.projectHealth || project.status || 'On Track',
    updatedAt: project.updatedAt || project.updated_at || project.modifiedAt || '-',
  };
};

export const normalizeProjectDetail = (project, id) => ({
  ...sampleDetail,
  id,
  title: project.title || project.name || project.projectName || sampleDetail.title,
  clientName: project.clientName || project.client_name || project.client || project.companyName || sampleDetail.clientName,
  owner: project.ownerName || project.owner || project.pic || project.createdBy || sampleDetail.owner,
  pic: project.pic || project.picName || project.contactName || '-',
  picUserId: project.picUserId || project.pic_user_id || '',
  health: project.health || project.statusHealth || project.projectHealth || project.status || sampleDetail.health,
  stage: normalizeStageLabel(project.stage || project.currentStage || project.current_stage || sampleDetail.stage),
  progress: clampProgress(project.progress ?? project.completionRate ?? project.completion_rate ?? sampleDetail.progress),
  auditDate: project.auditDate || project.audit_date || project.proposedAuditDate || sampleDetail.auditDate,
  deadline: project.deadline || project.dueDate || project.nextDeadline || sampleDetail.deadline,
  projectStartDate: project.projectStartDate || project.start_date || project.startDate || sampleDetail.projectStartDate,
  projectEndDate: project.projectEndDate || project.end_date || project.valid_until || project.validUntil || sampleDetail.projectEndDate,
  scope: project.scope || project.description || sampleDetail.scope,
  team: Array.isArray(project.team) ? project.team : [],
  tasks: Array.isArray(project.tasks) ? project.tasks : [],
  activity: Array.isArray(project.activity) ? project.activity : [],
});
